import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Brand, ThemeClasses } from "../types";
import { getTimeAgo, type TimeTranslations } from "../../../../../../utils/getDate";

interface ModelGridCardProps {
  brand: Brand;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onEdit: (brand: Brand) => void;
  onDetails: (brand: Brand) => void;
  onCommands: (brand: Brand) => void;
  t: (key: string, options?: any) => string;
}

export function ModelGridCard({
  brand,
  themeClasses,
  isDarkMode,
  onEdit,
  onDetails,
  onCommands,
  t,
}: ModelGridCardProps) {
  const { t: tTranslate } = useTranslation("lite");
  const hasCommands = brand.collections?.includes("commands");

  const timeTranslations: TimeTranslations = useMemo(() => ({
    noData: tTranslate("time.noData"),
    rightNow: tTranslate("time.rightNow"),
    min: tTranslate("time.min"),
    hour: tTranslate("time.hour"),
    hours: tTranslate("time.hours"),
    day: tTranslate("time.day"),
    days: tTranslate("time.days"),
    month: tTranslate("time.month"),
    months: tTranslate("time.months"),
    and: tTranslate("time.and"),
  }), [tTranslate]);

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-xl p-5 hover:shadow-lg transition-all`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div
            className={`w-10 h-10 ${isDarkMode ? "bg-blue-900/30" : "bg-blue-100"} text-blue-500 rounded-xl flex items-center justify-center`}
          >
            <i className="ri-cpu-line text-lg"></i>
          </div>
          <div>
            <h3 className={`font-semibold ${themeClasses.text}`}>
              {brand.brand || "N/A"}
            </h3>
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {brand.model || "N/A"}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {brand.origin && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              brand.origin === "v5"
                ? "bg-emerald-600 text-white dark:bg-emerald-500 dark:text-white"
                : brand.origin === "v4"
                ? "bg-green-500 text-white dark:bg-green-600 dark:text-white"
                : "bg-teal-400 text-teal-900 dark:bg-teal-600 dark:text-white"
            }`}>
              {brand.origin.toUpperCase()}
            </span>
          )}
          {hasCommands && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-600 text-white dark:bg-purple-500 dark:text-white">
              {t("models.hasCommands")}
            </span>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="space-y-3 mb-4">
        {/* Operational Alias */}
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("models.fields.operationalAlias")}:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {brand.operational_alias || t("models.noData")}
          </span>
        </div>

        {/* IP Address */}
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("models.fields.ipAddress")}:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {brand.network?.ip_address || t("models.noData")}
          </span>
        </div>

        {/* Port */}
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("models.fields.port")}:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {brand.network?.port || t("models.noData")}
          </span>
        </div>

        {/* Protocol */}
        <div className="flex items-center justify-between">
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("models.fields.protocol")}:
          </span>
          <span className={`text-sm ${themeClasses.text}`}>
            {brand.network?.protocol || t("models.noData")}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col space-y-2 mb-3">
        <div className="flex space-x-2">
          <button
            onClick={() => onEdit(brand)}
            className={`flex-1 py-2 px-3 ${themeClasses.buttonHover} border ${themeClasses.cardBorder} rounded-lg ${themeClasses.buttonText} ${themeClasses.buttonTextHover} text-sm font-medium transition-colors`}
          >
            <i className="ri-edit-line mr-1"></i>
            {t("models.actions.edit")}
          </button>
          <button
            onClick={() => onDetails(brand)}
            className="flex-1 py-2 px-3 bg-primary-500 hover:bg-primary-400 text-gray-900 rounded-lg text-sm font-medium transition-colors"
          >
            <i className="ri-eye-line mr-1"></i>
            {t("models.actions.details")}
          </button>
        </div>
        <button
          onClick={() => onCommands(brand)}
          className={`w-full py-2 px-3 ${isDarkMode ? "bg-purple-900/30 hover:bg-purple-900/50 text-purple-400" : "bg-purple-100 hover:bg-purple-200 text-purple-700"} rounded-lg text-sm font-medium transition-colors`}
        >
          <i className="ri-terminal-box-line mr-1"></i>
          {t("models.actions.commands")}
        </button>
      </div>

      {/* Last Update */}
      <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
        <p className={`text-xs ${themeClasses.textSecondary}`}>
          {t("models.lastUpdate")}:{" "}
          {brand.updatedAt?._seconds
            ? getTimeAgo(brand.updatedAt._seconds, timeTranslations)
            : "N/A"}
        </p>
      </div>
    </div>
  );
}
